import { Link } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface ArticleLayoutProps {
  title: string;
  breadcrumbs: BreadcrumbItem[];
  schemaMarkup: Record<string, unknown>[];
  heroImage: string;
  heroAlt: string;
  datePublished: string;
  readingTime: string;
  children: React.ReactNode;
}

export default function ArticleLayout({
  title,
  breadcrumbs,
  schemaMarkup,
  heroImage,
  heroAlt,
  datePublished,
  readingTime,
  children,
}: ArticleLayoutProps) {
  return (
    <div className="min-h-screen bg-background-50">
      {schemaMarkup.map((schema, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}

      {/* Navigation Bar */}
      <nav className="w-full bg-background-50/95 backdrop-blur-md border-b border-background-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 md:px-10 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="font-heading text-lg md:text-xl font-bold text-foreground-900 hover:text-primary-500 transition-colors"
          >
            Kakobuy Spreadsheet
          </Link>
          <div className="flex items-center gap-3 md:gap-4">
            <Link
              to="/"
              className="text-xs md:text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              Strona Główna
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1 md:gap-1.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-3 md:px-4 py-2 rounded-lg transition-all duration-300 whitespace-nowrap text-xs md:text-sm"
            >
              <i className="ri-file-list-3-line"></i>
              Kakobuy Sheet
            </a>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-4 md:px-10 py-3 md:py-4">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-foreground-500">
          {breadcrumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i > 0 && <i className="ri-arrow-right-s-line text-xs"></i>}
              {crumb.path ? (
                <Link to={crumb.path} className="hover:text-primary-500 transition-colors">
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-foreground-700 font-medium">{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>
      </div>

      {/* Article Content */}
      <main className="max-w-4xl mx-auto px-4 md:px-10 py-6 md:py-12">
        <article>
          <header className="mb-8 md:mb-10">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {datePublished}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {readingTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                Kakobuy Spreadsheet Polska
              </span>
            </div>
          </header>

          {/* Hero Image */}
          <div className="relative w-full h-[200px] md:h-[400px] rounded-2xl overflow-hidden mb-8 md:mb-10">
            <img
              src={heroImage}
              alt={heroAlt}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose-custom space-y-6">{children}</div>
        </article>
      </main>

      <Footer />
    </div>
  );
}